import { API } from '../../../services'
import type { APIResponse, Filters, Pagination } from '../../../services/types'
import { HttpStatusCode } from 'axios'
import { isSuccessStatus } from '../../../utils/http'
import { defaultPagination } from '../../../services/defaults'
import { defineStore } from 'pinia'
import { setPagination } from '../../../services/utils/utils'
import { useToast } from 'vuestic-ui'
import type {
  CreateInventoryLocationInput,
  InventoryLocationApi,
  UpdateInventoryLocationInput,
} from '../services/locations'

const { init } = useToast()

export const useInventoryLocationsStore = defineStore('inventoryLocationsStore', {
  state: () => ({
    loading: false,
    filters: {} as Filters,
    pagination: defaultPagination,
    sorting: { sortBy: 'name' as string | undefined, sortingOrder: 'asc' as 'asc' | 'desc' | null },
    locations: [] as InventoryLocationApi[],
  }),
  getters: {
    getLocations(state) {
      return state.locations
    },
  },
  actions: {
    initLoading() {
      this.loading = true
    },
    stopLoading() {
      this.loading = false
    },
    setLocations(locations: InventoryLocationApi[]) {
      this.locations = locations
    },
    setPagination(pagination: Pagination) {
      this.pagination = setPagination(pagination)
    },
    setCurrentPage(addPage: boolean = false) {
      const page = this.pagination?.page ?? 1
      this.pagination.page = addPage ? page + 1 : Math.max(1, page - 1)
    },
    addLocation(location: InventoryLocationApi) {
      this.locations.unshift(location)
    },
    editLocation(updated: InventoryLocationApi) {
      const i = this.locations.findIndex((l) => l.id === updated.id)
      if (i !== -1) {
        this.locations[i] = updated
      }
    },
    async dispatchGetLocations(filters?: Filters): Promise<{ success: boolean; data?: InventoryLocationApi[] }> {
      this.initLoading()

      const { status, data } = await API.inventoryLocations
        .getLocations(filters, this.pagination)
        .finally(this.stopLoading)

      if (status === HttpStatusCode.Ok) {
        this.setLocations(data.data)
        this.setPagination({
          meta: data.meta,
          page: data.current_page ?? data.meta?.current_page,
          perPage: data.per_page ?? data.meta?.per_page,
          total: data.total ?? data.meta?.total,
        })
      }

      return {
        success: status === HttpStatusCode.Ok,
        data: data.data,
      }
    },
    async dispatchNavigateToNextPage(addPage: boolean = false) {
      this.setCurrentPage(addPage)
    },
    async dispatchCreateLocation(input: CreateInventoryLocationInput): Promise<APIResponse<null>> {
      this.initLoading()

      const { status, data } = await API.inventoryLocations.createLocation(input).finally(this.stopLoading)

      if (isSuccessStatus(status) && data?.data) {
        this.addLocation(data.data)
        init({ message: 'Location created successfully', color: 'success' })
      }

      return { success: isSuccessStatus(status) }
    },
    async dispatchUpdateLocation(input: UpdateInventoryLocationInput): Promise<APIResponse<null>> {
      this.initLoading()

      const { status, data } = await API.inventoryLocations.updateLocation(input).finally(this.stopLoading)

      if (isSuccessStatus(status) && data?.data) {
        this.editLocation(data.data)
        init({ message: 'Location updated successfully', color: 'success' })
      }

      return { success: isSuccessStatus(status) }
    },
    async dispatchDeactivateLocation(location: InventoryLocationApi): Promise<APIResponse<null>> {
      this.initLoading()

      const { status, data } = await API.inventoryLocations.deactivateLocation(location.id).finally(this.stopLoading)

      if (isSuccessStatus(status) && data?.data) {
        this.editLocation(data.data)
        init({ message: 'Location deactivated', color: 'success' })
      }

      return { success: isSuccessStatus(status) }
    },
  },
})
